// StaffList.jsx - Complete Updated Version with Training Certificate Downloads and i18n
import React, { useState, useEffect } from 'react';
import { Container, Table, Card, Form, Button, Row, Col, Spinner, Badge, InputGroup, Modal, ListGroup } from 'react-bootstrap';
import {
    FileText,
    Download,
    Printer,
    ChevronUp,
    ChevronDown,
    ChevronsUpDown,
    Search,
    Users,
    Eye,
    User,
    MapPin,
    Mail,
    Briefcase,
    Home,
    CreditCard,
    GraduationCap,
    Building
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from "../AuthContext/AuthContext";
import { getAuthenticatedAxios } from '../Utilities/axiosConfiguration';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Staff Detail Modal Component
const StaffDetailModal = ({ show, onHide, staff, userRole }) => {
    const { t } = useTranslation('staffList');

    // ALL REACT HOOKS MUST BE AT THE TOP - BEFORE ANY RETURNS
    const [downloading, setDownloading] = useState(false);
    const [trainingCertificates, setTrainingCertificates] = useState([]);
    const [loadingCertificates, setLoadingCertificates] = useState(false);
    const [downloadingTraining, setDownloadingTraining] = useState(null);
    const axios = getAuthenticatedAxios();

    // Role checks (can be before early return since they don't depend on staff)
    const isHR = userRole && (
        userRole.toLowerCase().includes('hr') ||
        userRole.toLowerCase().includes('human') ||
        userRole.toLowerCase().includes('resource')
    );

    const isSecurity = userRole && (
        userRole.toLowerCase().includes('security')
    );

    const canViewTrainingCertificates = isHR || isSecurity;

    // useEffect MUST be before any conditional returns
    useEffect(() => {
        if (show && staff && canViewTrainingCertificates) {
            fetchTrainingCertificates();
        }
    }, [show, staff?.id, canViewTrainingCertificates]);

    // NOW we can do early return AFTER all hooks
    if (!staff) return null;

    // Debug logging - COMPREHENSIVE STAFF OBJECT
    console.log('=== STAFF OBJECT DEBUG ===');
    console.log('Full staff object:', staff);
    console.log('Duty station fields:');
    console.log('  - staff.duty_station:', staff.duty_station);
    console.log('  - staff.duty_station_name:', staff.duty_station_name);
    console.log('  - staff.dutyStation:', staff.dutyStation);
    console.log('  - staff.dutyStationName:', staff.dutyStationName);
    console.log('All staff keys:', Object.keys(staff));
    console.log('========================');

    // Debug logging
    console.log('StaffDetailModal - userRole:', userRole);
    console.log('StaffDetailModal - staff.proof_of_life:', staff?.proof_of_life);
    console.log('StaffDetailModal - isHR:', isHR);
    console.log('StaffDetailModal - isSecurity:', isSecurity);
    console.log('StaffDetailModal - canViewTrainingCertificates:', canViewTrainingCertificates);

    const fetchTrainingCertificates = async () => {
        if (!staff?.id) return;

        setLoadingCertificates(true);
        try {
            console.log('Fetching training certificates for user:', staff.id);
            const response = await axios.get(`/training/user/${staff.id}/download`);
            console.log('Training certificates fetched:', response.data);
            setTrainingCertificates(response.data);
        } catch (error) {
            console.error('Error fetching training certificates:', error);
            if (error.response?.status === 404) {
                setTrainingCertificates([]);
            } else if (error.response?.status === 403) {
                console.error('Access denied to training certificates');
                setTrainingCertificates([]);
            }
        } finally {
            setLoadingCertificates(false);
        }
    };

    const handleDownloadTrainingCertificate = async (trainingId, trainingName) => {
        setDownloadingTraining(trainingId);

        try {
            console.log('Downloading training certificate:', trainingId, 'for user:', staff.id);

            const downloadUrl = `/training/user/${staff.id}/training/${trainingId}/download`;
            console.log('Download URL:', downloadUrl);

            const response = await axios.get(downloadUrl, {
                responseType: 'blob',
            });

            console.log('Training certificate fetched successfully');

            // Create blob URL
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/pdf'
            });
            const blobUrl = window.URL.createObjectURL(blob);

            // Get filename from header or construct one
            let filename = `${staff.firstName}_${staff.lastName}_${trainingName.replace(/\s+/g, '_')}_certificate`;
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            } else {
                const contentType = response.headers['content-type'];
                if (!filename.includes('.')) {
                    const extension = contentType?.includes('pdf') ? '.pdf' :
                        contentType?.includes('image') ? '.jpg' : '.pdf';
                    filename += extension;
                }
            }

            // Create download link
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            console.log('Training certificate download initiated');

        } catch (error) {
            console.error('Download error:', error);

            let errorMessage = 'Failed to download training certificate. ';

            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        errorMessage += 'Certificate file not found.';
                        break;
                    case 403:
                        errorMessage += 'Access denied. Only HR and SECURITY can download certificates.';
                        break;
                    case 401:
                        errorMessage += 'Authentication required. Please log in again.';
                        break;
                    default:
                        errorMessage += `Server error: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage += 'No response from server. Check your connection.';
            } else {
                errorMessage += error.message;
            }

            alert(errorMessage);
        } finally {
            setDownloadingTraining(null);
        }
    };

    const DetailRow = ({ icon: Icon, label, value, className = "" }) => (
        <ListGroup.Item className={`border-0 py-3 ${className}`}>
            <div className="d-flex align-items-start">
                <div className="me-3 mt-1">
                    <Icon size={18} className="text-primary" />
                </div>
                <div className="flex-grow-1">
                    <div className="fw-semibold text-muted small mb-1">{label}</div>
                    <div className="text-dark">{value || <span className="text-muted fst-italic">No data</span>}</div>
                </div>
            </div>
        </ListGroup.Item>
    );

    const handleDownloadProofOfLife = async () => {
        if (!staff.proof_of_life) {
            alert(t('staffList.modal.proofOfLife.notAvailable'));
            return;
        }

        setDownloading(true);

        try {
            console.log('Downloading proof of life for user ID:', staff.id);
            console.log('Proof of life path in database:', staff.proof_of_life);

            // Use the secure backend download endpoint
            const downloadUrl = `/contact/download/proof-of-life/${staff.id}`;
            console.log('Using download endpoint:', downloadUrl);

            // Fetch the file with authentication
            const response = await axios.get(downloadUrl, {
                responseType: 'blob', // Important for file download
            });

            console.log('File fetched successfully:', response);

            // Create a blob URL
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/pdf'
            });
            const blobUrl = window.URL.createObjectURL(blob);

            // Create download link
            const link = document.createElement('a');
            link.href = blobUrl;

            // Get filename from Content-Disposition header or use default
            let filename = `proof_of_life_${staff.firstName}_${staff.lastName}`;
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            } else {
                // Add extension if not present
                const contentType = response.headers['content-type'];
                if (!filename.includes('.')) {
                    const extension = contentType?.includes('pdf') ? '.pdf' :
                        contentType?.includes('image') ? '.jpg' : '.pdf';
                    filename += extension;
                }
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            console.log('Download initiated successfully');

        } catch (error) {
            console.error('Download error:', error);

            let errorMessage = 'Failed to download proof of life document. ';

            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        errorMessage += 'File not found. The document may not have been uploaded yet.';
                        break;
                    case 403:
                        errorMessage += 'Access denied. Only HR personnel can download this document.';
                        break;
                    case 401:
                        errorMessage += 'Authentication required. Please log in again.';
                        break;
                    default:
                        errorMessage += `Server error: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage += 'No response from server. Check your connection.';
            } else {
                errorMessage += error.message;
            }

            alert(errorMessage);
        } finally {
            setDownloading(false);
        }
    };

    return (
        // UPDATED: Modal size changed from "lg" to "xl" (20% wider)
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title className="d-flex align-items-center">
                    <User size={24} className="me-2" />
                    {t('staffList.modal.title')} - {staff.firstName} {staff.lastName}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-0">
                <ListGroup variant="flush">
                    {/* Personal Information Section */}
                    <DetailRow
                        icon={User}
                        label={t('staffList.modal.fields.fullName')}
                        value={`${staff.firstName || ''} ${staff.lastName || ''}`.trim()}
                    />
                    <DetailRow
                        icon={User}
                        label={t('staffList.modal.fields.gender')}
                        value={staff.gender}
                        className="bg-light bg-opacity-25"
                    />
                    <DetailRow
                        icon={Mail}
                        label={t('staffList.modal.fields.email')}
                        value={staff.email}
                    />
                    <DetailRow
                        icon={Mail}
                        label={t('staffList.modal.fields.workEmail')}
                        value={staff.work_email}
                        className="bg-light bg-opacity-25"
                    />

                    {/* Employment Information Section */}
                    <DetailRow
                        icon={Briefcase}
                        label={t('staffList.modal.fields.function')}
                        value={staff.function}
                    />
                    <DetailRow
                        icon={Building}
                        label={t('staffList.modal.fields.unit')}
                        value={staff.unit}
                        className="bg-light bg-opacity-25"
                    />
                    <DetailRow
                        icon={Building}
                        label={t('staffList.modal.fields.section')}
                        value={staff.section}
                    />
                    <DetailRow
                        icon={MapPin}
                        label={t('staffList.modal.fields.dutyStation')}
                        value={staff.duty_station_name || staff.duty_station}
                        className="bg-light bg-opacity-25"
                    />
                    <DetailRow
                        icon={MapPin}
                        label={t('staffList.modal.fields.officeLocation')}
                        value={staff.office_location}
                    />
                    <DetailRow
                        icon={User}
                        label={t('staffList.modal.fields.employmentStatus')}
                        value={
                            staff.staff_status ? (
                                <Badge
                                    bg={
                                        staff.staff_status?.toLowerCase().includes('international') ? 'primary' :
                                            staff.staff_status?.toLowerCase().includes('national') ? 'success' :
                                                staff.staff_status?.toLowerCase().includes('unv') ? 'info' :
                                                    staff.staff_status?.toLowerCase() === 'active' ? 'success' : 'secondary'
                                    }
                                    className="px-3 py-2"
                                >
                                    {staff.staff_status}
                                </Badge>
                            ) : null
                        }
                        className="bg-light bg-opacity-25"
                    />
                    <DetailRow
                        icon={Briefcase}
                        label={t('staffList.modal.fields.modeOfWork')}
                        value={staff.mode_of_work}
                    />

                    {/* Additional Information Section */}
                    <DetailRow
                        icon={Users}
                        label={t('staffList.modal.fields.dependants')}
                        value={staff.dependants}
                        className="bg-light bg-opacity-25"
                    />
                    <DetailRow
                        icon={Home}
                        label={t('staffList.modal.fields.homeAddress')}
                        value={staff.home_address}
                    />
                    <DetailRow
                        icon={CreditCard}
                        label={t('staffList.modal.fields.passportNumber')}
                        value={staff.passport}
                        className="bg-light bg-opacity-25"
                    />

                    {/* Training Records Section with Download Functionality */}
                    <DetailRow
                        icon={GraduationCap}
                        label={t('staffList.modal.training.title')}
                        value={
                            staff.trainings && staff.trainings.length > 0 ? (
                                <div>
                                    <div className="mb-3">
                                        <Badge bg="info" className="px-3 py-2 me-2">
                                            {staff.trainings.length} {staff.trainings.length !== 1 ? t('staffList.modal.training.trainingsPlural') : t('staffList.modal.training.trainingsSingular')}
                                        </Badge>
                                        {canViewTrainingCertificates && loadingCertificates && (
                                            <Spinner animation="border" size="sm" variant="primary" />
                                        )}
                                    </div>
                                    <ul className="list-unstyled mb-0">
                                        {staff.trainings.map((training, index) => {
                                            // Find certificate info for this training
                                            const certInfo = trainingCertificates.find(
                                                cert => cert.training_name === training
                                            );

                                            return (
                                                <li key={index} className="mb-2 pb-2 border-bottom">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div className="flex-grow-1">
                                                            <span className="badge bg-light text-dark me-2">{index + 1}</span>
                                                            <span className="fw-medium">{training}</span>
                                                            {certInfo?.year_completed && (
                                                                <small className="text-muted ms-2">
                                                                    ({certInfo.year_completed})
                                                                </small>
                                                            )}
                                                        </div>
                                                        {canViewTrainingCertificates && certInfo?.has_certificate && (
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleDownloadTrainingCertificate(
                                                                    certInfo.training_id,
                                                                    certInfo.training_name
                                                                )}
                                                                disabled={downloadingTraining === certInfo.training_id}
                                                                className="ms-2"
                                                            >
                                                                {downloadingTraining === certInfo.training_id ? (
                                                                    <>
                                                                        <Spinner
                                                                            as="span"
                                                                            animation="border"
                                                                            size="sm"
                                                                            className="me-1"
                                                                        />
                                                                        {t('staffList.modal.training.downloading')}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Download size={14} className="me-1" />
                                                                        {t('staffList.modal.training.downloadCertificate')}
                                                                    </>
                                                                )}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ) : null
                        }
                    />

                    {/* Proof of Life Document Section - HR Only */}
                    {isHR && (
                        <DetailRow
                            icon={FileText}
                            label={t('staffList.modal.proofOfLife.title')}
                            value={
                                staff.proof_of_life ? (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleDownloadProofOfLife}
                                        disabled={downloading}
                                        className="d-flex align-items-center"
                                    >
                                        {downloading ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                />
                                                {t('staffList.modal.proofOfLife.downloading')}
                                            </>
                                        ) : (
                                            <>
                                                <Download size={14} className="me-2" />
                                                {t('staffList.modal.proofOfLife.download')}
                                            </>
                                        )}
                                    </Button>
                                ) : (
                                    <span className="text-muted fst-italic">{t('staffList.modal.proofOfLife.notAvailable')}</span>
                                )
                            }
                            className="bg-light bg-opacity-25"
                        />
                    )}
                </ListGroup>
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={onHide}>
                    {t('staffList.buttons.close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Main StaffList Component
const StaffList = () => {
    const { t } = useTranslation('staffList');
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const { isLoggedIn, logout, userRole } = useAuth();
    const axios = getAuthenticatedAxios();

    console.log('StaffList - userRole from AuthContext:', userRole);

    useEffect(() => {
        if (isLoggedIn) {
            fetchStaff();
        }
    }, [isLoggedIn]);

    const fetchStaff = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/staff_information/list');
            setStaff(response.data);
        } catch (error) {
            console.error('Error fetching staff:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedData = () => {
        let sortedData = [...staff];

        if (searchTerm) {
            sortedData = sortedData.filter(person =>
                Object.values(person).some(value =>
                    value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }

        if (sortConfig.key) {
            sortedData.sort((a, b) => {
                const aValue = a[sortConfig.key] || '';
                const bValue = b[sortConfig.key] || '';

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return sortedData;
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <ChevronsUpDown size={14} className="ms-1 text-muted" />;
        }
        return sortConfig.direction === 'asc' ?
            <ChevronUp size={14} className="ms-1 text-primary" /> :
            <ChevronDown size={14} className="ms-1 text-primary" />;
    };

    const handleViewDetails = (person) => {
        setSelectedStaff(person);
        setShowModal(true);
    };

    // NEW: Helper function to get badge variant for staff status
    const getStatusBadgeColor = (status) => {
        if (!status) return 'secondary';
        const statusLower = status.toLowerCase();
        if (statusLower.includes('international')) return 'primary';
        if (statusLower.includes('national')) return 'success';
        if (statusLower.includes('unv')) return 'info';
        if (statusLower.includes('active')) return 'success';
        return 'secondary';
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(staff);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, t('staffList.export.sheetName'));
        XLSX.writeFile(workbook, `${t('staffList.export.excelFileName')}.xlsx`);
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text(t('staffList.export.pdfTitle'), 14, 15);

        const tableData = staff.map(person => [
            person.firstName,
            person.lastName,
            person.email,
            person.function,
            person.unit
        ]);

        doc.autoTable({
            head: [[t('staffList.table.firstName'), t('staffList.table.lastName'), t('staffList.table.email'), t('staffList.table.function'), t('staffList.table.unit')]],
            body: tableData,
            startY: 25
        });

        doc.save(`${t('staffList.export.pdfFileName')}.pdf`);
    };

    const sortedData = getSortedData();

    if (!isLoggedIn) {
        return (
            <Container className="mt-5">
                <Card className="shadow-sm border-0">
                    <Card.Body className="text-center py-5">
                        <Users size={48} className="text-muted mb-3" />
                        <h5 className="text-muted">Please log in to view the staff directory.</h5>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa' }}>
            <Card className="shadow-sm border-0 rounded-3">
                {/* Enhanced Header */}
                <Card.Header className="bg-white border-0 py-4">
                    <Row className="align-items-center">
                        <Col md={6}>
                            <div className="d-flex align-items-center">
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                    style={{
                                        width: '48px',
                                        height: '48px',
                                        backgroundColor: '#0d6efd',
                                        boxShadow: '0 2px 8px rgba(13, 110, 253, 0.2)'
                                    }}
                                >
                                    <Users size={24} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="mb-0 fw-bold text-dark">{t('staffList.title')}</h3>
                                    <small className="text-muted">
                                        Manage and view all staff members
                                    </small>
                                </div>
                            </div>
                        </Col>
                        <Col md={6} className="text-end">
                            <Badge
                                bg="primary"
                                className="px-3 py-2 rounded-pill"
                                style={{ fontSize: '0.9rem', fontWeight: '500' }}
                            >
                                <Users size={14} className="me-2" />
                                {sortedData.length} Staff {sortedData.length === 1 ? 'Member' : 'Members'}
                            </Badge>
                        </Col>
                    </Row>
                </Card.Header>

                <Card.Body className="p-4">
                    {/* Search and Export Section */}
                    <Row className="mb-4">
                        <Col md={8}>
                            <InputGroup size="lg" className="shadow-sm">
                                <InputGroup.Text className="bg-white border-end-0">
                                    <Search size={20} className="text-muted" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder={t('staffList.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="border-start-0 ps-0"
                                    style={{ fontSize: '1rem' }}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={4} className="text-end">
                            <Button
                                variant="outline-success"
                                className="me-2 shadow-sm"
                                onClick={exportToExcel}
                                style={{ minWidth: '100px' }}
                            >
                                <FileText size={16} className="me-2" />
                                Excel
                            </Button>
                            <Button
                                variant="outline-danger"
                                className="shadow-sm"
                                onClick={exportToPDF}
                                style={{ minWidth: '100px' }}
                            >
                                <Printer size={16} className="me-2" />
                                PDF
                            </Button>
                        </Col>
                    </Row>

                    {/* Staff Table */}
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                            <p className="mt-3 text-muted fw-medium">{t('staffList.loadingStaff')}</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                                <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th
                                        onClick={() => handleSort('firstName')}
                                        style={{
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            color: '#495057',
                                            padding: '1rem',
                                            borderBottom: '2px solid #dee2e6'
                                        }}
                                    >
                                        {t('staffList.table.firstName')} {getSortIcon('firstName')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('lastName')}
                                        style={{
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            color: '#495057',
                                            padding: '1rem',
                                            borderBottom: '2px solid #dee2e6'
                                        }}
                                    >
                                        {t('staffList.table.lastName')} {getSortIcon('lastName')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('email')}
                                        style={{
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            color: '#495057',
                                            padding: '1rem',
                                            borderBottom: '2px solid #dee2e6'
                                        }}
                                    >
                                        {t('staffList.table.email')} {getSortIcon('email')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('function')}
                                        style={{
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            color: '#495057',
                                            padding: '1rem',
                                            borderBottom: '2px solid #dee2e6'
                                        }}
                                    >
                                        {t('staffList.table.function')} {getSortIcon('function')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('unit')}
                                        style={{
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            color: '#495057',
                                            padding: '1rem',
                                            borderBottom: '2px solid #dee2e6'
                                        }}
                                    >
                                        {t('staffList.table.unit')} {getSortIcon('unit')}
                                    </th>
                                    {/* NEW: Training column */}
                                    <th
                                        onClick={() => handleSort('training_count')}
                                        className="text-center"
                                        style={{
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            color: '#495057',
                                            padding: '1rem',
                                            borderBottom: '2px solid #dee2e6'
                                        }}
                                    >
                                        <GraduationCap size={16} className="me-1" />
                                        {t('staffList.table.trainings')} {getSortIcon('training_count')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('staff_status')}
                                        className="text-center"
                                        style={{
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            color: '#495057',
                                            padding: '1rem',
                                            borderBottom: '2px solid #dee2e6'
                                        }}
                                    >
                                        {t('staffList.table.status')} {getSortIcon('staff_status')}
                                    </th>
                                    <th
                                        className="text-center"
                                        style={{
                                            fontWeight: '600',
                                            color: '#495057',
                                            padding: '1rem',
                                            borderBottom: '2px solid #dee2e6'
                                        }}
                                    >
                                        {t('staffList.table.actions')}
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                {sortedData.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5">
                                            <Users size={48} className="text-muted mb-3" />
                                            <p className="text-muted mb-0">{t('staffList.noData.title')}</p>
                                            <small className="text-muted">{t('staffList.noData.subtitle')}</small>
                                        </td>
                                    </tr>
                                ) : (
                                    sortedData.map((person, index) => (
                                        <tr
                                            key={person.id}
                                            style={{
                                                borderBottom: '1px solid #f0f0f0',
                                                transition: 'all 0.2s ease'
                                            }}
                                            className="table-row-hover"
                                        >
                                            <td style={{ padding: '1rem', fontWeight: '500' }}>
                                                {person.firstName}
                                            </td>
                                            <td style={{ padding: '1rem', fontWeight: '500' }}>
                                                {person.lastName}
                                            </td>
                                            <td style={{ padding: '1rem' }}>
                                                <div className="d-flex align-items-center">
                                                    <Mail size={14} className="text-muted me-2" />
                                                    <span className="text-muted">{person.email}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '1rem' }}>
                                                <span className="text-dark">{person.function || '-'}</span>
                                            </td>
                                            <td style={{ padding: '1rem' }}>
                                                <span className="text-dark">{person.unit || '-'}</span>
                                            </td>
                                            {/* NEW: Training count column */}
                                            <td className="text-center" style={{ padding: '1rem' }}>
                                                {person.training_count > 0 ? (
                                                    <Badge
                                                        bg="warning"
                                                        text="dark"
                                                        className="px-3 py-2 rounded-pill"
                                                        style={{ fontSize: '0.85rem', fontWeight: '600' }}
                                                    >
                                                        <GraduationCap size={14} className="me-1" />
                                                        {person.training_count}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            {/* UPDATED: Colored status badges */}
                                            <td className="text-center" style={{ padding: '1rem' }}>
                                                {person.staff_status ? (
                                                    <Badge
                                                        bg={getStatusBadgeColor(person.staff_status)}
                                                        className="px-3 py-2 rounded-pill"
                                                        style={{ fontSize: '0.8rem', fontWeight: '500' }}
                                                    >
                                                        {person.staff_status}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td className="text-center" style={{ padding: '1rem' }}>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleViewDetails(person)}
                                                    className="px-3 py-2 rounded-pill shadow-sm"
                                                    style={{ fontWeight: '500' }}
                                                >
                                                    <Eye size={14} className="me-1" />
                                                    {t('staffList.buttons.viewDetails')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Staff Detail Modal */}
            <StaffDetailModal
                show={showModal}
                onHide={() => setShowModal(false)}
                staff={selectedStaff}
                userRole={userRole}
            />

            <style jsx global>{`
                .table-row-hover:hover {
                    background-color: #f8f9fa !important;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                    transform: translateY(-1px);
                }
            `}</style>
        </Container>
    );
};

export default StaffList;